import React from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { DateField, InlineMessage, SaveBar, SectionCard, StatusChip } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import type { StorePreorderState } from '../../types/storePreorderGoLive';
import type { StorePreorderGoLiveState } from './useStorePreorderGoLive';

const T = 'storePreorderGoLive';
const NOTE_MAX = 200;

const formatDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

interface StorePreorderGoLiveEditorProps {
  state: StorePreorderGoLiveState;
}

/**
 * "When this product goes live" on the store product details (flag `store_preorder_go_live`,
 * MOT-766): the store chooses when its own listing takes orders for an announced product (a
 * go-live day, or Take orders now) and what shoppers are told about availability. Shown only for
 * an announced or pre-order product, or one whose own region date is only a year or month. Edits
 * are saved with the dirty-only save bar; a member who is not an owner or admin sees it read-only.
 */
export function StorePreorderGoLiveEditor({ state }: StorePreorderGoLiveEditorProps) {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const view = state.view;
  if (state.loading || !view) {
    return state.error ? (
      <SectionCard dense padded title={t(`${T}.title`)}>
        <InlineMessage severity="error">{t(`${T}.loadError`)}</InlineMessage>
      </SectionCard>
    ) : null;
  }
  if (!view.shown) return null;

  const ignored = view.eligibility === 'catalog_lifecycle' || view.eligibility === 'released';
  const disabled = !view.canEdit || state.saving || view.eligibility === 'catalog_lifecycle';
  const { draft } = state;

  const save = async () => {
    try {
      await state.save();
      showToast(t(`${T}.saved`), 'success');
    } catch (error) {
      showToast((error as Error).message || t(`${T}.saveFailed`), 'error');
    }
  };

  const describe = (s: StorePreorderState) =>
    s.preorder ? (s.message || t(`${T}.preorderNoText`)) : t(`${T}.notTakingOrders`);

  const catalogLine = view.catalog.periodOnly
    ? t(`${T}.catalogPeriodOnly`)
    : view.catalog.releaseDate
      ? t(`${T}.catalogDate`, { date: formatDay(view.catalog.releaseDate) })
      : t(`${T}.catalogNoDate`);

  return (
    <SectionCard
      dense
      padded
      title={t(`${T}.title`)}
      subtitle={t(`${T}.subtitle`)}
      actions={view.setting ? <StatusChip tone="info" label={t(`${T}.chipSet`)} /> : undefined}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">{catalogLine}</Typography>
        {view.eligibility === 'catalog_lifecycle' && (
          <InlineMessage severity="info">{t(`${T}.catalogLifecycle`)}</InlineMessage>
        )}
        {view.eligibility === 'released' && (
          <InlineMessage severity="info">{t(`${T}.released`)}</InlineMessage>
        )}
        {!view.canEdit && (
          <Typography variant="caption" color="text.secondary">{t(`${T}.readOnly`)}</Typography>
        )}

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={draft.orderNow}
                disabled={disabled}
                onChange={(e) => {
                  state.set('orderNow', e.target.checked);
                  if (e.target.checked) state.set('opensAt', '');
                }}
              />
            }
            label={t(`${T}.orderNow`)}
          />
          <Typography variant="caption" color="text.secondary" component="div">{t(`${T}.orderNowHint`)}</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <DateField
            label={t(`${T}.opensAt`)}
            value={draft.orderNow ? '' : draft.opensAt}
            onChange={(v) => state.set('opensAt', v)}
            disabled={disabled || draft.orderNow}
            size="small"
            helperText={view.timeZone ? t(`${T}.opensAtHintZone`, { zone: view.timeZone }) : t(`${T}.opensAtHint`)}
          />
          <DateField
            label={t(`${T}.availabilityDate`)}
            value={draft.availabilityDate}
            onChange={(v) => state.set('availabilityDate', v)}
            disabled={disabled}
            size="small"
            error={state.problem === 'dateOrder'}
            helperText={state.problem === 'dateOrder' ? t(`${T}.dateOrder`) : t(`${T}.availabilityDateHint`)}
          />
        </Box>

        <TextField
          label={t(`${T}.availabilityNote`)}
          value={draft.availabilityNote}
          onChange={(e) => state.set('availabilityNote', e.target.value.slice(0, NOTE_MAX))}
          disabled={disabled}
          size="small"
          fullWidth
          placeholder={t(`${T}.availabilityNotePlaceholder`)}
          error={state.problem === 'noteRequired' || state.problem === 'datePassed'}
          helperText={
            state.problem === 'noteRequired'
              ? t(`${T}.noteRequired`)
              : state.problem === 'datePassed'
                ? t(`${T}.datePassed`)
                : t(`${T}.availabilityNoteHint`)
          }
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="overline" color="text.secondary">{t(`${T}.yourListing`)}</Typography>
          <Typography variant="body2">
            {ignored || !view.setting ? describe(view.catalogState) : describe(view.effectiveState)}
          </Typography>
          <Typography variant="caption" color="text.secondary">{t(`${T}.onlyYourStore`)}</Typography>
        </Box>

        {view.canEdit && view.setting && (
          <Box>
            <Button size="small" onClick={state.clear} disabled={state.saving}>{t(`${T}.clear`)}</Button>
          </Box>
        )}
      </Stack>
      <SaveBar
        dirty={state.dirty}
        message={t(`${T}.saveMessage`)}
        saving={state.saving}
        onSave={() => void save()}
        onDiscard={state.discard}
      />
    </SectionCard>
  );
}
